import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@repo/ui/card'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@repo/ui/form'
import { useFormValidator, useT } from '@repo/ui/hooks'
import { Textarea } from '@repo/ui/textarea'
import { TriangleAlertIcon } from 'lucide-react'
import { useMemo } from 'react'
import { toast } from 'sonner'
import { type ZodError } from 'zod'

import { ResetAndUpdate } from '@/components/ResetAndUpdate'
import { promptSchema } from '@/validation-schemas'

interface PromptEditorProps {
  description: string
  initialPrompt: string
  onPromptUpdated: (prompt: string) => void
  placeholder: string
  title: string
  validateOutput?: (output: string) => true | string | ZodError
}

export function PromptEditorLight({
  description,
  initialPrompt,
  onPromptUpdated,
  placeholder,
  title,
  validateOutput = () => true,
}: PromptEditorProps) {
  const { tc } = useT()

  const defaultValues = useMemo(() => ({ prompt: initialPrompt }), [initialPrompt])

  const { form, isFormValid, submit, submitOnPressMetaEnter } = useFormValidator({
    defaultValues,
    onSubmit: (values) => {
      const validationResult = validateOutput(values.prompt)
      if (validationResult === true) onPromptUpdated(values.prompt)
      else
        toast(
          tc('invalidValueFormat', {
            value:
              typeof validationResult === 'object' && 'message' in validationResult
                ? validationResult.message
                : validationResult,
          }),
          {
            closeButton: true,
            dismissible: true,
            icon: <TriangleAlertIcon className="size-5 text-red-500" />,
          },
        )
    },
    schema: {
      prompt: promptSchema,
    },
    customValidation: (values) => {
      const errors: { [key: string]: string } = {}
      const error = promptSchema.safeParse(values.prompt).error?.message
      if (error) errors.prompt = error
      return errors
    },
  })

  const handleReset = () => {
    form.reset({ prompt: initialPrompt })
  }
  return (
    <Card className="flex h-full w-full flex-col">
      <CardHeader className="flex-shrink-0">
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>

      <Form {...form}>
        <form onSubmit={submit} onKeyDown={submitOnPressMetaEnter} className="flex flex-1 flex-col">
          <CardContent className="flex flex-1 flex-col">
            <FormField
              control={form.control}
              name="prompt"
              render={({ field }) => (
                <FormItem className="flex flex-1 flex-col">
                  <FormControl className="flex-1">
                    <Textarea
                      id="prompt"
                      placeholder={placeholder}
                      className="flex-1 resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>

          <CardFooter className="flex-shrink-0 justify-end">
            <ResetAndUpdate onReset={handleReset} onUpdate={submit} disabled={!isFormValid} />
          </CardFooter>
        </form>
      </Form>
    </Card>
  )
}
